import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {prependAOrAn} from 'BenchlingUtil';
import {SchemaSummary} from 'constants/RegistryConstants';
import {SequenceTopology, SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT} from 'constants/SequenceConstants';
import {BioentityInfo, BioentitySummary} from 'folder-items/FileConstants';
import getConfig from 'getConfig';
import StorableChip from 'inventory/chips/StorableChip';
import SampleRestrictionStatusTypedValueSelector from 'inventory/sample-status/SampleRestrictionStatusTypedValueSelector';
import LookupConfigCellDropdown from 'lab-automation/settings/automation-input-file-config/LookupConfigCellDropdown';
import PlateLayoutCellDropdown from 'lab-automation/settings/automation-input-file-config/PlateLayoutCellDropdown';
import BenchLink from 'link-preview/BenchLink';
import {SUMMARIZE_THRESHOLD_DEFAULT} from 'metadata/TagValueViewer';
import {parseConjugationSitesTypedValue} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteSerialization';
import AdcConjugationSitesCell from 'molbio/adcs/registration-table/AdcConjugationSitesCell';
import AdcConjugationSitesDisplay from 'molbio/adcs/registration-table/AdcConjugationSitesDisplay';
import AdcLinkerPayloadLeavingGroupCell from 'molbio/adcs/registration-table/AdcLinkerPayloadLeavingGroupCell';
import BulkAssemblyBinSelector from 'molbio/bulk-assembly/tables/BulkAssemblyBinSelector';
import BulkAssemblyConstructFragmentSelector from 'molbio/bulk-assembly/tables/BulkAssemblyConstructFragmentSelector';
import BulkAssemblyConstructName from 'molbio/bulk-assembly/tables/BulkAssemblyConstructName';
import BulkAssemblyFragmentEndpoint from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentEndpoint';
import {BulkAssemblyFragmentsStructuredTableRowMetadata} from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentsStructuredTableAdapter';
import BulkAssemblyPrimer from 'molbio/bulk-assembly/tables/BulkAssemblyPrimer';
import BulkAssemblyPrimerBases from 'molbio/bulk-assembly/tables/BulkAssemblyPrimerBases';
import BulkAssemblyTableRestrictionEnzymeSelector from 'molbio/bulk-assembly/tables/BulkAssemblyTableRestrictionEnzymeSelector';
import BulkAssemblyValidationStatus from 'molbio/bulk-assembly/tables/BulkAssemblyValidationStatus';
import {BlobLinkListInTable} from 'notebook/data-tables/BlobLinkList';
import {reformatMinMaxString} from 'notebook/integrations/procedures/ProceduresUtil';
import {SnapshotChip, SnapshotChipList, SnapshotChipProps} from 'notebook/mediocre/core-ui/SnapshotChip';
import MultiSelectOptionSearchCellDropdown from 'notebook/plain-tables/selectors/MultiSelectOptionSearchCellDropdown';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import OptionSearchCellDropdown from 'notebook/plain-tables/selectors/OptionSearchCellDropdown';
import {isBooleanValueColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {UnitReferenceColumnDisplayMode} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import getMaxChipsToDisplay from 'notebook/tables/export/getMaxChipsToDisplay';
import SelectorTagValueViewer, {
  SelectorInternalLink,
} from 'notebook/tables/typed-values/components/viewers/SelectorTagValueViewer';
import StorablePlaceholdersTableCell from 'notebook/tables/typed-values/components/viewers/StorablePlaceholdersTableCell';
import TagValueTypedValueViewer from 'notebook/tables/typed-values/components/viewers/TagValueTypedValueViewer';
import {
  convertTypedValueBlob,
  formatTypedValue,
  getBatchInfoFromTypedValue,
  getBioentityInfoFromTypedValue,
  getBioentityInfosFromTypedValue,
  getBioentityTargetFileIdsFromTypedValue,
  getPartyIdsFromTypedValue,
  getProcessOutputIdFromTypedValue,
  getProcessTaskIdFromCell,
  isTypedValueEmpty,
  resolveTypedValueToBoolean,
} from 'notebook/tables/typed-values/DataTypes';
import {
  ArrayTypedValue,
  BaseTypedValueType,
  TypedValue,
  TypedValueType,
} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  BatchInfo,
  BioentityTypedValue,
  BlobLinkSchemaFieldTypedValue,
  EntryLinkTypedValue,
  LookupConfigInfoTypedValue,
  PlateLayoutTypedValue,
  SelectorSchemaFieldTypedValue,
  StageRunSampleInfo,
  StorableInfo,
  StringTypedValue,
  StudiesTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {ContainersColumnMode} from 'notebook/transfer-tables/TransferTableConstants';
import ProcedureSchemaFieldTypeSelectorForTables from 'procedures/shared-components/schema-field-type-selectors/ProcedureSchemaFieldTypeSelectorForTables';
import ParameterUsageCell from 'procedures/tables/ParameterUsageCell';
import {ProcedureConditionReplicateCellDisplay} from 'procedures/tables/ProcedureConditionReplicateColumn';
import {renderProcessTaskAssigneeAvatar} from 'processes/tables/ProcessTaskAssigneeAvatar';
import ProcessTaskStatusSelector from 'processes/tables/ProcessTaskStatusSelector';
import {FULFILLMENT_STATUS_ICONS, FULFILLMENT_STATUS_NAMES} from 'requests/fulfillments/FulfillmentConstants';
import TestDefinitionChip from 'sample-tests/ui/test-definitions/chips/TestDefinitionChip';
import {BaseFillPlateTransferStructuredTableRowMetadata} from 'samples/plate-maps/transfer-table/BaseFillPlateTransferStructuredTableAdapter';
import AnonymousTagValue from 'samples/plate-maps/transfer-table/cell-renderers/AnonymousTagValue';
import FillPlateExperimentalRole from 'samples/plate-maps/transfer-table/cell-renderers/FillPlateExperimentalRole';
import FillPlateTransferSourceChip from 'samples/plate-maps/transfer-table/cell-renderers/FillPlateTransferSourceChip';
import FillPlateTransferSourceContainerChip from 'samples/plate-maps/transfer-table/cell-renderers/FillPlateTransferSourceContainerChip';
import FillPlateTransferSourceIcon from 'samples/plate-maps/transfer-table/cell-renderers/FillPlateTransferSourceIcon';
import FillPlateTransferSourceSchemaChip from 'samples/plate-maps/transfer-table/cell-renderers/FillPlateTransferSourceSchemaChip';
import MaterialInputSelector from 'samples/plate-maps/transfer-table/cell-renderers/MaterialInputSelector';
import {MULTIPLE_VALUES_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import FillPlateTransferMeasurementAmount from 'samples/plate-maps/transfer-table/FillPlateTransferTableMeasurementAmount';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {restBlobToGraphQL} from 'schemas/tags/BlobUtils';
import BooleanValueCellEditor, {BooleanCellDisplayMode} from 'schemas/tags/BooleanValueCellEditor';
import {fieldTypeSupportsLegalText} from 'schemas/tags/LegalTextSelectors';
import {isProcedureReadOnlyForCell} from 'studies/search/ProcedureColumnUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import BioentityChip from 'ui-library/chips/BioentityChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList, {BaseChipList} from 'ui-library/chips/ChipList';
import {LOCKED_ICON_CHIP_PROPS} from 'ui-library/chips/ChipUtil';
import EntryChip from 'ui-library/chips/EntryChip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import ProcessOutputChip from 'ui-library/chips/ProcessOutputChip';
import ProcessTaskChip from 'ui-library/chips/ProcessTaskChip';
import StudyChip from 'ui-library/chips/StudyChip';
import DatetimeValue from 'ui-library/DatetimeValue';
import DateValue from 'ui-library/DateValue';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import UrlAwareText from 'ui-library/UrlAwareText';
import UnitAwareNumberTagValueViewer from 'units/UnitAwareNumberTagValueViewer';
import isValidLink from 'util/isValidLink';
import {ellipsize} from 'util/NameUtil';
import {assertNever, Optional} from 'util/Types';

import {BasesColumnEntityType} from '../bases-columns/BasesColumnConstants';
import {
  LastSyncedUpstreamValue,
  StructuredTableColumnGroups,
  StructuredTableKind,
} from '../StructuredTableConstants';
import StructuredTableContext from '../util/StructuredTableContext';
import {parseTopologyFromString} from './TopologyColumnParsers';

export type FormattedCellValue = JSX.Element | string | null;

const MAYBE_CONTAINS_URL_REGEX = /:\/\//;
const ELLIPSIS_TEXT_STYLE: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function shouldWrapText(columnConstraint: ColumnConstraint | null): boolean {
  const {columnDefinition} = columnConstraint ?? {};
  // Raw sequences can be hundreds of characters long, so they stay on one line and are edited in the
  // multi-line cell editor instead.
  if (columnDefinition?.type === TableColumnType.PROTEIN_NESTED_COMPONENT) {
    return false;
  }
  // These are the only other column types which support controlling text wrap, all others wrap
  // unconditionally.
  if (
    columnDefinition?.type !== TableColumnType.STRING &&
    columnDefinition?.type !== TableColumnType.EXISTING_BIOENTITY
  ) {
    return true;
  }
  return columnDefinition.wrapText ?? true;
}

export interface CellStateInfo {
  locked?: boolean;
  cellLocked?: Optional<boolean>;
  isDoubleSelected?: boolean;
  isSelected?: boolean;
  commitCell?: (typedValue: TypedValue) => void;
  setCustomSelectorElement?: (elem: JSX.Element) => void;
  handleSelectCell?: () => void;
  handleDoubleClick?: () => void;
  cellSelectorHandler?: (text: string) => void;
  lastSyncedUpstreamValue?: LastSyncedUpstreamValue;
  hasRowBeenInView?: Optional<boolean>;
  isExportView?: boolean;
}

export const TOTAL_LOCK_CELL_STATE: CellStateInfo = {
  // None of the other properties are relevant if the cell is completely locked
  locked: true,
  cellLocked: true,
  isDoubleSelected: false,
};

/** specifies the display label the chip should use when frozen chips are enabled */
function getDisplayNameOverride(
  chipInfo:
    | BatchInfo
    | BioentitySummary
    | BioentityInfo
    | EntryLinkTypedValue
    | StorableInfo
    | StageRunSampleInfo
): string | null {
  if (getConfig('ENABLE_FROZEN_CHIPS') === false) {
    return null;
  }
  if ('displayLabel' in chipInfo) {
    return chipInfo.displayLabel ?? null;
  }
  if ('batch' in chipInfo && chipInfo.batch) {
    return chipInfo.batch.name;
  }
  return chipInfo.name;
}

/**
 * Returns an indiciator icon for multi-select cells that are selected and editable.
 */
export function getMultiSelectIcon(
  isMulti: boolean,
  isSelected: boolean,
  locked: boolean,
  handleClick?: () => void
): JSX.Element | null {
  if (isMulti && isSelected && !locked) {
    return (
      <BenchlingIcon
        className="internalLink chip-container chip--tableCell"
        style={{padding: '3px 3px', cursor: 'pointer'}}
        icon="create"
        onClick={handleClick}
      />
    );
  }
  return null;
}

/**
 * Return a custom component for this cell, or null if we should fall back to the default behavior.
 */
function formattedCellValueFromColumnConstraint(
  columnConstraint: ColumnConstraint,
  cell: Cell,
  typedValue: TypedValue,
  cellStateInfo: CellStateInfo,
  rowMetadata: RowMetadata | null,
  fileId: string
): FormattedCellValue {
  const {
    locked,
    isDoubleSelected,
    isSelected,
    commitCell,
    setCustomSelectorElement,
    handleSelectCell,
    handleDoubleClick,
    cellSelectorHandler,
    lastSyncedUpstreamValue,
    isExportView = false,
  } = cellStateInfo;

  const {columnDefinition} = columnConstraint;
  if (!columnDefinition) {
    return null;
  }

  if (isBooleanValueColumnDefinition(columnDefinition)) {
    return (
      <div className="u-text-align-center">
        <input
          type="checkbox"
          checked={typedValue.type === BaseTypedValueType.BOOLEAN && typedValue.booleanValue}
          disabled={locked}
          onChange={
            locked || !commitCell
              ? undefined
              : (e) => {
                  commitCell({
                    type: BaseTypedValueType.BOOLEAN,
                    booleanValue: e.target.checked,
                  });
                }
          }
        />
      </div>
    );
  }
  switch (columnDefinition.type) {
    case TableColumnType.ALIAS:
      // Alias cells render a ChipList from an array of strings
      if (
        assertAndContinue(
          typedValue.type === BaseTypedValueType.ARRAY &&
            typedValue.rawValue.every((value) => value.type === BaseTypedValueType.STRING),
          'Alias cells must have a string or an array of strings.',
          {typedValue},
          {
            fingerprint: [
              'benchling',
              'data-input',
              'structured-table',
              'alias-cell',
              'alias-cell-string-values',
            ],
          }
        )
      ) {
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(2, isExportView)}
            chips={(typedValue as ArrayTypedValue).rawValue
              .filter((aliasTypedValue) => (aliasTypedValue as StringTypedValue).rawValue.length > 0)
              .map((aliasTypedValue) => (
                <Chip size={ChipSize.TABLE_CELL} text={(aliasTypedValue as StringTypedValue).rawValue} />
              ))}
            chipSize={ChipSize.TABLE_CELL}
          />
        );
      } else {
        return null;
      }
    case TableColumnType.BATCH: {
      const batchInfo = getBatchInfoFromTypedValue(typedValue);
      if (batchInfo) {
        return <span>{batchInfo.id}</span>;
      } else if (isTypedValueEmpty(typedValue)) {
        return <span className="cell-contents-newBatch">Pending</span>;
      } else {
        return null;
      }
    }
    case TableColumnType.BIOENTITY_NAME:
      return null;
    case TableColumnType.STUDIES: {
      if (typedValue.type === BaseTypedValueType.STUDIES) {
        return <StudyChipList studies={typedValue.studies} isExportView={isExportView} />;
      } else {
        return null;
      }
    }
    case TableColumnType.TEST_DEFINITION: {
      if (typedValue.type === BaseTypedValueType.TEST_DEFINITION) {
        const commonProps = {
          id: typedValue.id,
          size: ChipSize.TABLE_CELL,
        } satisfies Partial<SnapshotChipProps>;
        return (
          <SnapshotChip chipType="TestDefinitionChip" {...commonProps}>
            <TestDefinitionChip {...commonProps} />
          </SnapshotChip>
        );
      } else {
        return null;
      }
    }
    case TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR: {
      if (typedValue.type === BaseTypedValueType.LIBRARY_PARAMETER_DEFINITION) {
        return <Chip text={typedValue.name} size={ChipSize.TABLE_CELL} icon="parameters" />;
      }
      return null;
    }
    case TableColumnType.DNA_BASES:
    case TableColumnType.RNA_BASES:
    case TableColumnType.DNA_HELM:
    case TableColumnType.RNA_HELM:
    case TableColumnType.DNA_IDT:
    case TableColumnType.RNA_IDT:
      return null;
    case TableColumnType.BASES:
      if (
        [BasesColumnEntityType.DNA_OLIGO, BasesColumnEntityType.RNA_OLIGO].includes(
          columnDefinition.basesColumnConfig.entityType
        )
      ) {
        return null;
      } else if (
        [BaseTypedValueType.STRING, FieldType.LONG_TEXT, FieldType.TEXT].includes(
          typedValue.type as TypedValueType
        )
      ) {
        return ellipsize(formatTypedValue(typedValue), 10);
      } else {
        assertAndContinue(false, 'Sequence bases cells must be string typed values.', {
          typedValue,
        });
        return null;
      }
    case TableColumnType.SMILES:
      return null;
    case TableColumnType.CONCENTRATION:
      return null;
    case TableColumnType.MOLECULE_VERSION: {
      // The Molecule Version column persists a self-contained VersionedBioentityTypedValue (its own
      // TypedValue variant, BaseTypedValueType.MOLECULE_VERSION) -- see MoleculeVersionCustomEval -- but
      // renders as the pinned version's "Last updated" timestamp via a DatetimeValue chip, consistent
      // with the timezone-aware, second-precision display used elsewhere for datetime typed values.
      return typedValue.type === BaseTypedValueType.MOLECULE_VERSION ? (
        <DatetimeValue datetimeValue={typedValue.modifiedAt} showTimezoneOffset={false} />
      ) : null;
    }
    case TableColumnType.ADC_CONJUGATION_SITES: {
      // Text that is not a serialized site list (e.g. pasted) displays as-is; the column's custom eval
      // flags it.
      const sites = parseConjugationSitesTypedValue(typedValue);
      if (sites == null) {
        return null;
      }
      return locked ? (
        <AdcConjugationSitesDisplay sites={sites} />
      ) : (
        <AdcConjugationSitesCell
          sites={sites}
          onOpenDropdown={handleSelectCell}
          onUpdate={cellSelectorHandler ?? noop}
        />
      );
    }
    case TableColumnType.ADC_LINKER_PAYLOAD_R_GROUP:
      // A locked cell displays its leaving group label as plain text.
      return locked ? null : (
        <AdcLinkerPayloadLeavingGroupCell
          leavingGroupText={formatTypedValue(typedValue)}
          onOpenDropdown={handleSelectCell}
          onUpdate={cellSelectorHandler ?? noop}
        />
      );
    case TableColumnType.CUSTOM: {
      const {dataType} = columnDefinition.customColumnConfig;
      switch (dataType) {
        // display the values as normal
        case CustomColumnDataType.TEXT:
        case CustomColumnDataType.INTEGER:
        case CustomColumnDataType.FLOAT:
          return null;
        // If displaying as a checkbox, return a checkbox editor
        case CustomColumnDataType.BOOLEAN: {
          const resolvedTypedValue = resolveTypedValueToBoolean(typedValue).resolvedValue;
          const booleanValue =
            resolvedTypedValue.type === BaseTypedValueType.BOOLEAN ? resolvedTypedValue.booleanValue : null;
          switch (columnDefinition.customColumnConfig.displayMode) {
            case BooleanCellDisplayMode.CHECKBOX:
              return (
                <BooleanValueCellEditor
                  value={booleanValue}
                  onUpdate={(tag) => {
                    if (tag.booleanValue != null && commitCell && !locked) {
                      commitCell({
                        type: BaseTypedValueType.BOOLEAN,
                        booleanValue: tag.booleanValue,
                      });
                    }
                  }}
                  displayMode={BooleanCellDisplayMode.CHECKBOX}
                  locked={locked}
                />
              );
            case BooleanCellDisplayMode.DROPDOWN:
            default:
              return null;
          }
        }
        // display as date and time chips
        case CustomColumnDataType.DATE:
          return typedValue.type === BaseTypedValueType.DATE ? (
            <DateValue dateValue={typedValue.value} />
          ) : null;
        case CustomColumnDataType.DATETIME:
          return typedValue.type === BaseTypedValueType.DATETIME ? (
            <DatetimeValue datetimeValue={typedValue.value} />
          ) : null;
        default:
          assertNever(dataType);
          return null;
      }
    }
    case TableColumnType.CONTAINERS: {
      if (typedValue.type === BaseTypedValueType.STORABLES) {
        return (
          <span>
            {typedValue.storableSummaries.map((storableSummary) => {
              const commonProps = {
                id: storableSummary.id,
                size: ChipSize.TABLE_CELL,
                displayNameOverride: getDisplayNameOverride(storableSummary),
              } satisfies Partial<SnapshotChipProps>;
              return (
                <SnapshotChip chipType="StorableChip" key={storableSummary.id} {...commonProps}>
                  <StorableChip {...commonProps} />
                </SnapshotChip>
              );
            })}
          </span>
        );
      }

      // Transfer table, CREATE mode
      if (
        columnDefinition.containersColumnMode === ContainersColumnMode.NEW_DESTINATION_FOR_TRANSFER_TABLE &&
        isTypedValueEmpty(typedValue)
      ) {
        return (
          <span className="placeholderText">
            {columnConstraint.structuredTableColumn != null &&
            isReadOnly(columnConstraint.structuredTableColumn)
              ? '(Autogenerated)'
              : 'Enter a barcode...'}
          </span>
        );
      }

      // Legacy transfer table
      if (
        columnDefinition.containersColumnMode === ContainersColumnMode.LEGACY &&
        typedValue.type === BaseTypedValueType.STORABLE_PLACEHOLDERS
      ) {
        // Bottom half of the table
        return <StorablePlaceholdersTableCell typedValue={typedValue} />;
      }

      return null;
    }
    case TableColumnType.DEDICATED_LOOKUP:
      return formattedCellValueForLookupColumn(typedValue, fileId, columnConstraint, isExportView);
    case TableColumnType.BIOENTITY_SELECTOR: {
      const bioentityIds = getBioentityTargetFileIdsFromTypedValue(typedValue);
      if (bioentityIds) {
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(2, isExportView)}
            chips={bioentityIds.map((bioentityId) => (
              <BioentityChip id={bioentityId} size={ChipSize.TABLE_CELL} />
            ))}
            chipSize={ChipSize.TABLE_CELL}
          />
        );
      }
      return null;
    }
    case TableColumnType.EXISTING_BIOENTITY: {
      const bioentityInfos = getBioentityInfosFromTypedValue(typedValue);
      if (bioentityInfos) {
        if (bioentityInfos.length === 1) {
          const {id} = bioentityInfos[0];
          const commonProps = {
            id,
            size: ChipSize.TABLE_CELL,
            displayNameOverride: getDisplayNameOverride(bioentityInfos[0]),
          } satisfies Partial<SnapshotChipProps>;
          return (
            <SnapshotChip chipType="BioentityChip" {...commonProps}>
              <BioentityChip {...commonProps} />
            </SnapshotChip>
          );
        }
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(2, isExportView)}
            chips={bioentityInfos.map((bioentityInfo) => {
              const commonProps = {
                id: bioentityInfo.id,
                size: ChipSize.TABLE_CELL,
                displayNameOverride: getDisplayNameOverride(bioentityInfo),
              } satisfies Partial<SnapshotChipProps>;
              return (
                <SnapshotChip chipType="BioentityChip" key={bioentityInfo.id} {...commonProps}>
                  <BioentityChip {...commonProps} />
                </SnapshotChip>
              );
            })}
            chipSize={ChipSize.TABLE_CELL}
          />
        );
      } else if (isTypedValueEmpty(typedValue)) {
        // Placeholder text
        return (
          <StructuredTableContext.Consumer>
            {({structuredTableKind}) => {
              switch (structuredTableKind) {
                case StructuredTableKind.REGISTRATION:
                // The EXISTING_BIOENTITY column type is only used for structured tables
                // and legacy registration tables. If we are not in a structured table,
                // we must be in a legacy registration table.
                // eslint-disable-next-line no-fallthrough
                case null:
                  assert(
                    columnDefinition.type === TableColumnType.EXISTING_BIOENTITY,
                    'Unexpected to find a BIOENTITY SELECTOR column here'
                  );
                  return (
                    <span
                      className="placeholderText"
                      {...getTestProps({component: 'FormattedCellValue', element: 'placeholderText'})}
                    >
                      <RegistrationTableExistingBioentityCellPlaceholder
                        structuredTableKind={structuredTableKind}
                        tagSchemaOrInterface={columnDefinition.tagSchemaOrInterface}
                        isReadOnly={columnConstraint.structuredTableColumn!.isReadOnly}
                      />
                    </span>
                  );
                case StructuredTableKind.TRANSFER:
                  return (
                    <span className="placeholderText">
                      {columnConstraint.structuredTableColumn!.isReadOnly ? '' : 'Choose an entity...'}
                    </span>
                  );
                case StructuredTableKind.MIXTURE_INGREDIENTS:
                  if (
                    columnConstraint.structuredTableColumn!.columnGroup ===
                    StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE
                  ) {
                    // Prevent the component lot entity column from having placeholder text
                    return null;
                  }
                  return (
                    <span className="placeholderText">
                      {columnConstraint.structuredTableColumn!.isReadOnly ? '' : 'Choose a component...'}
                    </span>
                  );

                default:
                  return null;
              }
            }}
          </StructuredTableContext.Consumer>
        );
      } else {
        return null;
      }
    }
    case TableColumnType.FULFILLABLE_STATUS: {
      if (typedValue.type === BaseTypedValueType.FULFILLABLE_STATUS && typedValue.status) {
        const status = typedValue.status;
        const icon = FULFILLMENT_STATUS_ICONS[status];
        return (
          <div className="u-text-align-center">
            <Tooltip text={FULFILLMENT_STATUS_NAMES[status]}>
              <BenchlingIcon icon={icon.icon} className={icon.className} />
            </Tooltip>
          </div>
        );
      } else {
        return null;
      }
    }
    case TableColumnType.MISSING_FIELD:
      return null;
    case TableColumnType.NUMBER_OF_CONTAINERS:
      return null;
    case TableColumnType.PARTIES: {
      const partyIds = getPartyIdsFromTypedValue(typedValue);
      if (partyIds.length > 0) {
        return (
          <ChipList
            maxChips={getMaxChipsToDisplay(2, isExportView)}
            chipSize={ChipSize.TABLE_CELL}
            itemIds={partyIds}
          />
        );
      } else {
        return null;
      }
    }
    case TableColumnType.PARENT_STORABLE:
    case TableColumnType.BOX:
    case TableColumnType.PLATE: {
      return renderStorableSummaryChipsOrNull(typedValue);
    }
    case TableColumnType.POSITION: {
      // Hack to force left alignment when the position is a number. Need to use a div and not a span
      return <div className="text-left">{formatTypedValue(typedValue)}</div>;
    }
    case TableColumnType.TAG_VALUE: {
      const {schemaFieldSelectorId, legalTextSelectorId, isMulti} = columnDefinition.schemaField;
      const fieldType = columnDefinition.tagSchemaFieldType;

      // TODO(BNCH-698): change to assert once we have a DB constraint and verified that we are not hitting this error
      assertAndContinue(
        fieldType != null,
        'tagSchemaFieldType is expected to be defined for TAG_VALUE columns.'
      );
      if (
        fieldType != null &&
        fieldTypeSupportsLegalText(fieldType) &&
        (typedValue as SelectorSchemaFieldTypedValue).schemaFieldSelectorOptionIds != null
      ) {
        // Short-circuit since the default renderer for legal-text field types (e.g. float) won't handle
        // legal-text dropdown options.
        return (
          <SelectorTagValueViewer
            typedValue={typedValue}
            textValue={cell.text}
            schemaFieldSelectorId={legalTextSelectorId}
          />
        );
      }

      const isExpectedBooleanValue =
        fieldType === SchemaFieldConstants.FIELD_TYPES.BOOLEAN.type &&
        typedValue.type === BaseTypedValueType.BOOLEAN;

      if (
        isMulti &&
        cell.typedValue?.type === BaseTypedValueType.ARRAY &&
        cell.typedValue.rawValue[0]?.type === fieldType
      ) {
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(CHIP_DISPLAY_LIMIT_TABLE_ROW, isExportView)}
            chips={cell.typedValue.rawValue.map((value, i) => (
              <Chip key={i} size={ChipSize.TABLE_CELL} text={formatTypedValue(value)} />
            ))}
            chipSize={ChipSize.TABLE_CELL}
            isVertical={false}
          />
        );
      }

      if (
        typedValue.type !== fieldType &&
        !isExpectedBooleanValue &&
        typedValue.type !== BaseTypedValueType.COMPUTING
      ) {
        const formattedValue = formatTypedValue(typedValue, cell);
        // Empty multi-select cells have fieldType string, but should still show the icon
        if (formattedValue === '' && fieldType === SchemaFieldConstants.FIELD_TYPES.SELECTOR.type) {
          return getMultiSelectIcon(isMulti, Boolean(isSelected), Boolean(locked), handleDoubleClick);
        }
        // The type of the typed value doesn't match the column, most likely typed value was pasted from a
        // different column
        return <span>{formattedValue}</span>;
      }

      switch (fieldType) {
        case SchemaFieldConstants.FIELD_TYPES.BLOB_LINK.type: {
          const targetBlobs =
            (typedValue as BlobLinkSchemaFieldTypedValue).targetBlobs !== undefined
              ? (typedValue as BlobLinkSchemaFieldTypedValue).targetBlobs
              : [];
          return (
            <SnapshotChipList
              documentId={fileId}
              size={ChipSize.TABLE_CELL}
              ids={targetBlobs.map(({id}) => id)}
              className="chip-container"
            >
              <BlobLinkListInTable
                blobs={targetBlobs.map((blob) => restBlobToGraphQL(convertTypedValueBlob(blob)))}
                chipSize={ChipSize.TABLE_CELL}
                owningEntityApiId={fileId}
              />
            </SnapshotChipList>
          );
        }
        case SchemaFieldConstants.FIELD_TYPES.SELECTOR.type:
          return (
            <>
              <SelectorTagValueViewer
                typedValue={typedValue}
                textValue={cell.text}
                schemaFieldSelectorId={schemaFieldSelectorId}
              />
              {getMultiSelectIcon(isMulti, Boolean(isSelected), Boolean(locked), handleDoubleClick)}
            </>
          );
        case SchemaFieldConstants.FIELD_TYPES.AA_PART_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.BASE_MOLECULE_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.BATCH_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.BIOENTITY_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.DATE.type:
        case SchemaFieldConstants.FIELD_TYPES.DATETIME.type:
        case SchemaFieldConstants.FIELD_TYPES.ENTRY_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.PART_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.STORABLE_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.TRANSCRIPTION_LINK.type:
        case SchemaFieldConstants.FIELD_TYPES.TRANSLATION_LINK.type:
          return (
            <TagValueTypedValueViewer
              chipSize={ChipSize.TABLE_CELL}
              shouldPreviewImages={false}
              showChipsAsLinks={true}
              summarize={false}
              summarizeThreshold={SUMMARIZE_THRESHOLD_DEFAULT}
              typedValue={typedValue}
              textValue=""
              schemaFieldRenderData={{
                name: columnDefinition.tagSchemaFieldDisplayName,
                fieldType,
              }}
              shouldShowDateAndDatetimesAsChips={true}
              isExportView={isExportView}
              fileId={fileId}
            />
          );
        case SchemaFieldConstants.FIELD_TYPES.BOOLEAN.type: {
          const resolvedTypedValue = resolveTypedValueToBoolean(typedValue).resolvedValue;
          const value =
            resolvedTypedValue.type === BaseTypedValueType.BOOLEAN ? resolvedTypedValue.booleanValue : null;
          return (
            <BooleanValueCellEditor
              value={value}
              onUpdate={(tag: {value: string; booleanValue: boolean | null}) => {
                if (commitCell && !locked) {
                  commitCell({type: FieldType.BOOLEAN, value: tag.value, booleanValue: tag.booleanValue});
                }
              }}
              isSelected={Boolean(isDoubleSelected)}
              locked={Boolean(locked)}
            />
          );
        }
        case SchemaFieldConstants.FIELD_TYPES.INTEGER.type:
        case SchemaFieldConstants.FIELD_TYPES.FLOAT.type:
          if (
            columnConstraint.structuredTableColumn &&
            'unitApiIdentifier' in columnConstraint.structuredTableColumn &&
            columnConstraint.structuredTableColumn.unitApiIdentifier
          ) {
            return (
              <UnitAwareNumberTagValueViewer
                value={formatTypedValue(typedValue, cell)}
                unitApiIdentifier={columnConstraint.structuredTableColumn.unitApiIdentifier}
              />
            );
          }
          return null;
        default:
          return null;
      }
    }
    case TableColumnType.STAGE_RUN: {
      if (setCustomSelectorElement && !locked) {
        setCustomSelectorElement(
          <OptionCellDropdown
            locked={Boolean(locked)}
            isSelected={Boolean(isDoubleSelected)}
            // eslint-disable-next-line no-restricted-syntax
            options={_.pluck(columnDefinition.stageRuns, 'name')}
            onOpenDropdown={handleSelectCell}
            onUpdate={cellSelectorHandler ?? noop}
          />
        );
      }
      return formatTypedValue(typedValue);
    }
    case TableColumnType.SAMPLE:
      if (typedValue.type === BaseTypedValueType.BATCH) {
        return <span>{typedValue.batchInfo.id}</span>;
      } else if (typedValue.type === BaseTypedValueType.BIOENTITY) {
        const bioentitySummary = typedValue.bioentitySummary;
        const commonProps = {
          id: bioentitySummary.id,
          size: ChipSize.TABLE_CELL,
          displayNameOverride: getDisplayNameOverride(bioentitySummary),
        } satisfies Partial<SnapshotChipProps>;
        return (
          <SnapshotChip chipType="BioentityChip" {...commonProps}>
            <BioentityChip {...commonProps} />
          </SnapshotChip>
        );
      }
      return formatTypedValue(typedValue);
    case TableColumnType.SAMPLE_GROUP_SAMPLE:
      if (typedValue.type === BaseTypedValueType.STAGE_RUN_SAMPLE) {
        const {stageRunSample} = typedValue;
        if (stageRunSample.fileId) {
          const commonProps = {
            id: stageRunSample.fileId,
            size: ChipSize.TABLE_CELL,
            displayNameOverride: getDisplayNameOverride(stageRunSample),
          } satisfies Partial<SnapshotChipProps>;
          return (
            <SnapshotChip chipType="BioentityChip" {...commonProps}>
              <BioentityChip {...commonProps} />
            </SnapshotChip>
          );
        } else if (stageRunSample.batch) {
          return <span>{stageRunSample.batch.id}</span>;
        }
      }
      return formatTypedValue(typedValue);
    case TableColumnType.SAMPLE_RESTRICTION_STATUS: {
      if (typedValue.type === BaseTypedValueType.SAMPLE_RESTRICTION_STATUS) {
        return (
          <SampleRestrictionStatusTypedValueSelector
            typedValue={typedValue}
            onUpdate={commitCell ?? noop}
            locked={locked}
          />
        );
      }
      return null;
    }
    case TableColumnType.LOOKUP_CONFIG:
      return (
        <LookupConfigCellDropdown
          typedValue={typedValue as LookupConfigInfoTypedValue}
          locked={false}
          isSelected={false}
          onOpenDropdown={handleSelectCell ?? noop}
          onUpdate={commitCell ?? noop}
          fullWidth={true}
        />
      );
    case TableColumnType.PLATE_LAYOUT_SELECTOR:
      return (
        <PlateLayoutCellDropdown
          typedValue={typedValue as PlateLayoutTypedValue}
          onOpenDropdown={handleSelectCell ?? noop}
          onUpdate={commitCell ?? noop}
          isSelected={false}
        />
      );
    case TableColumnType.OPTION_SELECTOR:
      return locked ? null : (
        <OptionCellDropdown
          staticDisplay={formatTypedValue(typedValue)}
          locked={false}
          isSelected={false}
          options={columnDefinition.options}
          onOpenDropdown={handleSelectCell}
          onUpdate={cellSelectorHandler ?? noop}
          fullWidth={true}
        />
      );
    case TableColumnType.OPTION_SEARCH_SELECTOR:
      if (locked) {
        return null;
      }
      if (
        columnDefinition.schemaField?.fieldType === SchemaFieldConstants.FIELD_TYPES.SELECTOR.type ||
        columnDefinition.schemaFieldSelectorId != null
      ) {
        return (
          <SelectorTagValueViewer
            typedValue={typedValue}
            textValue={cell.text}
            schemaFieldSelectorId={columnDefinition.schemaFieldSelectorId}
          />
        );
      } else if (columnDefinition.isMulti) {
        const selectedOptions =
          typedValue.type === BaseTypedValueType.ARRAY
            ? typedValue.rawValue.map((value) => formatTypedValue(value))
            : [formatTypedValue(typedValue)];
        return (
          <MultiSelectOptionSearchCellDropdown
            options={columnDefinition.options}
            selectedOptions={selectedOptions}
            onOpenDropdown={handleSelectCell}
            isSelected={false}
            onUpdate={(newSelectedOptions: Array<string>) => {
              if (cellSelectorHandler) {
                cellSelectorHandler(newSelectedOptions.join(', '));
              }
            }}
          />
        );
      } else {
        return (
          <OptionSearchCellDropdown
            staticDisplay={formatTypedValue(typedValue)}
            isSelected={false}
            options={columnDefinition.options}
            emptyOptionsDisplay={columnDefinition.emptyOptionsDisplay}
            onOpenDropdown={handleSelectCell}
            onUpdate={cellSelectorHandler ?? noop}
            fullWidth={true}
          />
        );
      }
    case TableColumnType.UNIT_SELECTOR:
      return locked ? null : (
        <OptionCellDropdown
          staticDisplay={formatTypedValue(typedValue)}
          locked={false}
          isSelected={false}
          options={columnDefinition.acceptedUnits}
          onOpenDropdown={handleSelectCell}
          onUpdate={cellSelectorHandler ?? noop}
          fullWidth={true}
        />
      );
    case TableColumnType.MEASUREMENT_AMOUNT:
      return null;
    case TableColumnType.QUANTITY:
      return null;
    case TableColumnType.STRING:
      return null;
    case TableColumnType.PROCESS_TASK: {
      const taskId =
        typedValue.type === BaseTypedValueType.PROCESS_TASK ? typedValue.id : getProcessTaskIdFromCell(cell);
      if (taskId && taskId.length > 0) {
        return <ProcessTaskChip id={taskId} size={ChipSize.TABLE_CELL} />;
      }
      return null;
    }
    case TableColumnType.PROCESS_TASK_STATUS:
      return (
        <ProcessTaskStatusSelector
          lastSyncedUpstreamValue={lastSyncedUpstreamValue?.value}
          typedValue={typedValue}
          statusLifecycle={columnDefinition.statusLifecycle}
          onUpdate={commitCell ?? noop}
          locked={locked}
        />
      );
    case TableColumnType.PROCESS_TASK_ASSIGNEE:
      return renderProcessTaskAssigneeAvatar(typedValue);
    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
      return typedValue.type === BaseTypedValueType.ENTRY ? (
        <SnapshotChip
          chipType="EntryChip"
          id={typedValue.id}
          size={ChipSize.TABLE_CELL}
          displayNameOverride={getDisplayNameOverride(typedValue)}
          useSecondaryText={BenchlingConfig.ELN_ENABLE_IDS_IN_ENTRY_CHIPS}
        >
          <EntryChip
            id={typedValue.id}
            size={ChipSize.TABLE_CELL}
            displayNameOverride={getDisplayNameOverride(typedValue)}
          />
        </SnapshotChip>
      ) : null;
    case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
      return typedValue.type === BaseTypedValueType.DATE ? <DateValue dateValue={typedValue.value} /> : null;
    case TableColumnType.PROCESS_OUTPUT: {
      const outputId = getProcessOutputIdFromTypedValue(typedValue);
      if (outputId && outputId.length > 0) {
        return <ProcessOutputChip id={outputId} size={ChipSize.TABLE_CELL} />;
      }
      return null;
    }
    // the procedure chip is not clickable when the cell is unlocked, which diverges from the normal chip behavior
    case TableColumnType.PROCEDURE: {
      if (typedValue.type === BaseTypedValueType.PROCEDURE) {
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(CHIP_DISPLAY_LIMIT_TABLE_ROW, isExportView)}
            isVertical={false}
            chips={typedValue.procedures.map((procedure, index) => {
              const isReadOnly = isProcedureReadOnlyForCell(procedure.id, rowMetadata, columnDefinition);
              const commonProps = {
                id: procedure.id,
                size: ChipSize.TABLE_CELL,
              } satisfies Partial<SnapshotChipProps>;
              return (
                <SnapshotChip chipType="ProcedureChip" key={index} {...commonProps}>
                  <ProcedureChip
                    {...commonProps}
                    isLink={locked}
                    {...(isReadOnly ? LOCKED_ICON_CHIP_PROPS : {})}
                    flowchartVersionId={procedure.flowchartVersionId}
                  />
                </SnapshotChip>
              );
            })}
            chipSize={ChipSize.TABLE_CELL}
          />
        );
      } else {
        return null;
      }
    }
    case TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN:
      return (
        <ParameterUsageCell
          typedValue={typedValue}
          navigateToStep={columnDefinition.navigateToStep}
          propertyLabel={columnDefinition.name}
        />
      );
    case TableColumnType.PROCEDURE_PARAMETER_MIN_MAX: {
      const reformatted = reformatMinMaxString(typedValue, rowMetadata);
      if (reformatted == null) {
        return null;
      }
      return <>{reformatted}</>;
    }
    case TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE:
      if (typedValue.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE) {
        return (
          <ProcedureSchemaFieldTypeSelectorForTables
            typedValue={typedValue}
            onUpdate={noop}
            staticDisplay={true}
            context={columnDefinition.context}
          />
        );
      }
      return null;
    case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
      if (typedValue.type === BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES) {
        // for now, we only expect a single Condition/Replicate, although the typed value supports multiple
        assert(typedValue.conditionReplicates.length === 1, 'Expected only a single Condition-Replicate');
        const conditionReplicate = typedValue.conditionReplicates[0];
        const {conditionName, replicateNumber} = conditionReplicate;
        return (
          <ProcedureConditionReplicateCellDisplay
            conditionName={conditionName}
            replicateNumber={replicateNumber}
          />
        );
      }
      return null;
    case TableColumnType.INGREDIENT_CATALOG_ID:
    case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
    case TableColumnType.INGREDIENT_NOTES:
      return null;
    case TableColumnType.PROTEIN_NESTED_COMPONENT:
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER:
    case TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER: {
      if (typedValue.type !== BaseTypedValueType.BIOENTITY) {
        return null;
      }
      const bioentitySummary = typedValue.bioentitySummary;
      return (
        <BioentityChip
          id={bioentitySummary.id}
          size={ChipSize.TABLE_CELL}
          displayNameOverride={getDisplayNameOverride(bioentitySummary)}
        />
      );
    }
    case TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR:
      return (
        <BulkAssemblyBinSelector
          typedValue={typedValue}
          onUpdate={commitCell ?? noop}
          bins={columnDefinition.bins}
          locked={locked ?? false}
        />
      );
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT:
      return (
        <BulkAssemblyFragmentEndpoint
          typedValue={typedValue}
          onUpdate={commitCell ?? noop}
          rowMetadata={
            rowMetadata != null ? (rowMetadata as BulkAssemblyFragmentsStructuredTableRowMetadata) : null
          }
          locked={locked ?? false}
          side={columnDefinition.side}
        />
      );
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME:
      return <BulkAssemblyConstructName typedValue={typedValue} />;
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR:
      return (
        <BulkAssemblyConstructFragmentSelector
          typedValue={typedValue}
          onUpdate={commitCell ?? noop}
          fragmentDatas={columnDefinition.fragmentDatas ?? []}
          binColor={columnDefinition.binColor}
          locked={locked ?? false}
          method={columnDefinition.method}
          assemblyId={columnDefinition.assemblyId}
        />
      );
    case TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS:
      return <BulkAssemblyValidationStatus typedValue={typedValue} />;
    case TableColumnType.BULK_ASSEMBLY_PRIMER:
      return <BulkAssemblyPrimer typedValue={typedValue} />;
    case TableColumnType.BULK_ASSEMBLY_PRIMER_BASES:
      return <BulkAssemblyPrimerBases typedValue={typedValue} />;
    case TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME:
      return (
        <BulkAssemblyTableRestrictionEnzymeSelector
          typedValue={typedValue}
          onUpdate={commitCell ?? noop}
          isDisabled={locked ?? false}
        />
      );
    case TableColumnType.BULK_ASSEMBLY_LENGTH:
      return null;
    case TableColumnType.ANONYMOUS_TAG_VALUE:
      return <AnonymousTagValue typedValue={typedValue} />;
    case TableColumnType.EXPERIMENTAL_ROLE:
      return <FillPlateExperimentalRole typedValue={typedValue} />;
    case TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT:
      return (
        <FillPlateTransferMeasurementAmount
          typedValue={typedValue}
          isMultipleValues={cell.text === MULTIPLE_VALUES_SENTINEL}
        />
      );
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP:
      return (
        <FillPlateTransferSourceChip
          typedValue={typedValue}
          frozenEntityChipData={columnDefinition.frozenEntityChipData}
          reagentGroupsById={columnDefinition.reagentGroupsById}
          testOrderInfoById={columnDefinition.testOrderInfoById}
        />
      );
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP:
      return (
        <FillPlateTransferSourceContainerChip
          typedValue={typedValue}
          frozenEntityChipData={columnDefinition.frozenEntityChipData}
          reagentGroupsById={columnDefinition.reagentGroupsById}
          testOrderInfoById={columnDefinition.testOrderInfoById}
        />
      );
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON:
      return <FillPlateTransferSourceIcon typedValue={typedValue} />;
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP:
      return <FillPlateTransferSourceSchemaChip typedValue={typedValue} />;
    case TableColumnType.PLATE_MAP_MATERIAL_INPUT:
      return (
        <MaterialInputSelector
          typedValue={typedValue}
          locked={locked ?? false}
          onUpdate={commitCell ?? noop}
          plateMaterialInputs={columnDefinition.plateMaterialInputs}
          schemaId={(rowMetadata as BaseFillPlateTransferStructuredTableRowMetadata).schemaId}
        />
      );
    case TableColumnType.UNIT_REFERENCE:
      if (typedValue.type === BaseTypedValueType.UNIT_REFERENCE) {
        if (columnDefinition.displayMode === UnitReferenceColumnDisplayMode.SYMBOL) {
          return (
            <Tooltip text={typedValue.unitSummary.name}>
              <span>{typedValue.unitSummary.symbol}</span>
            </Tooltip>
          );
        }
        return (
          <Chip
            text={`${typedValue.unitSummary.name} (${typedValue.unitSummary.symbol})`}
            icon="ruler"
            size={ChipSize.TABLE_CELL}
          />
        );
      } else {
        return null;
      }
    case TableColumnType.COMPLEX_POLYMER_COMPONENT: {
      if (typedValue.type === BaseTypedValueType.ARRAY) {
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(2, isExportView)}
            chips={typedValue.rawValue.map((bioentityValue: BioentityTypedValue) => (
              <BioentityChip id={bioentityValue.bioentitySummary.id} size={ChipSize.TABLE_CELL} />
            ))}
            chipSize={ChipSize.TABLE_CELL}
          />
        );
      } else if (typedValue.type === BaseTypedValueType.STRING && typedValue.rawValue === '') {
        // Display a chip list when there is an empty string.
        return (
          <BaseChipList
            maxChips={getMaxChipsToDisplay(2, isExportView)}
            chips={[]}
            chipSize={ChipSize.TABLE_CELL}
          />
        );
      } else {
        // Otherwise, display the raw text.
        return null;
      }
    }
    case TableColumnType.COMPLEX_POLYMER_CONNECTIONS:
      return null;
    case TableColumnType.OLIGO_DUPLEX_OFFSET:
      return null;
    case TableColumnType.SEQUENCE_TOPOLOGY: {
      if (typedValue.type !== BaseTypedValueType.STRING) {
        return null;
      }
      const selectedTopology = parseTopologyFromString(typedValue.rawValue);
      if (selectedTopology == null) {
        return null;
      }
      return (
        <OptionCellDropdown
          staticDisplay={SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT[selectedTopology]}
          locked={locked ?? false}
          isSelected={false}
          options={[
            SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT[SequenceTopology.LINEAR],
            SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT[SequenceTopology.CIRCULAR],
          ]}
          onOpenDropdown={handleSelectCell}
          onUpdate={
            cellSelectorHandler != null
              ? (text) => {
                  cellSelectorHandler(parseTopologyFromString(text) ?? '');
                }
              : noop
          }
          fullWidth={true}
        />
      );
    }
    case TableColumnType.ANTIBODY_DOMAIN: {
      if (typedValue.type === BaseTypedValueType.BIOENTITY) {
        return <BioentityChip id={typedValue.bioentitySummary.id} size={ChipSize.TABLE_CELL} />;
      } else {
        return null;
      }
    }
    default: {
      return null;
    }
  }
}

export function StudyChipList({
  studies,
  isExportView = false,
}: {
  studies: StudiesTypedValue['studies'];
  isExportView?: boolean;
}): JSX.Element {
  return (
    <BaseChipList
      maxChips={getMaxChipsToDisplay(2, isExportView)}
      chips={studies.map(({id}) => {
        const commonProps = {id, size: ChipSize.TABLE_CELL} satisfies Partial<SnapshotChipProps>;
        return (
          <SnapshotChip chipType="StudyChip" key={id} {...commonProps}>
            <StudyChip {...commonProps} />
          </SnapshotChip>
        );
      })}
      chipSize={ChipSize.TABLE_CELL}
    />
  );
}

function renderStorableSummaryChipsOrNull(typedValue: TypedValue): JSX.Element | null {
  if (typedValue.type === BaseTypedValueType.STORABLES && typedValue.storableSummaries.length > 0) {
    return (
      <span>
        {typedValue.storableSummaries.map((storableSummary) => {
          const commonProps = {
            id: storableSummary.id,
            size: ChipSize.TABLE_CELL,
            displayNameOverride: getDisplayNameOverride(storableSummary),
          } satisfies Partial<SnapshotChipProps>;
          return (
            <SnapshotChip chipType="StorableChip" key={storableSummary.id} {...commonProps}>
              <StorableChip {...commonProps} />
            </SnapshotChip>
          );
        })}
      </span>
    );
  } else {
    return null;
  }
}

/**
 * Return a custom element for the typed value (independent of column constraints), or null if we should fall
 * back to the default render behavior. This method is specific to values rendered in Lookup Column cells.
 */
function formattedCellValueForLookupColumn(
  typedValue: TypedValue,
  fileId: string,
  columnConstraint: ColumnConstraint,
  isExportView: boolean
): FormattedCellValue {
  switch (typedValue.type) {
    case BaseTypedValueType.ARRAY: {
      if (
        typedValue.rawValue.length > 0 &&
        typedValue.rawValue.every(
          (value): value is StudiesTypedValue => value.type === BaseTypedValueType.STUDIES
        )
      ) {
        const allStudies = typedValue.rawValue.flatMap((value) => value.studies);
        return <StudyChipList studies={allStudies} isExportView={isExportView} />;
      }

      const subvalues = typedValue.rawValue.map((value) =>
        formattedCellValueForLookupColumn(value, fileId, columnConstraint, isExportView)
      );

      // if this method returned null for any value in the array, assume the array contains a type that we
      // don't know how to render and fall back to the default rendering strategy.
      if (subvalues.includes(null)) {
        return null;
      }

      // Ensure that all elements have unique keys
      const keyedSubvalues = subvalues.map((value, i) => {
        const subvalue = subvalues[i];
        if (React.isValidElement(subvalue)) {
          return React.cloneElement(subvalue, {key: i});
        } else {
          return subvalue;
        }
      });
      return <span>{keyedSubvalues}</span>;
    }
    case BaseTypedValueType.BATCH: {
      return <span>{typedValue.batchInfo.id}</span>;
    }
    case BaseTypedValueType.BIOENTITY: {
      const bioentitySummary = getBioentityInfoFromTypedValue(typedValue);
      if (bioentitySummary) {
        const {id} = bioentitySummary;
        return (
          <BioentityChip
            id={id}
            key={id}
            size={ChipSize.TABLE_CELL}
            displayNameOverride={getDisplayNameOverride(bioentitySummary)}
          />
        );
      }
      return null;
    }
    case BaseTypedValueType.DATETIME: {
      if (typedValue.value == null || typedValue.value.length === 0) {
        return null;
      }
      return <DatetimeValue datetimeValue={typedValue.value} />;
    }
    case BaseTypedValueType.DATE: {
      if (typedValue.value == null || typedValue.value.length === 0) {
        return null;
      }
      return <DateValue dateValue={typedValue.value} />;
    }
    case BaseTypedValueType.ENTRY: {
      return (
        <EntryChip
          id={typedValue.id}
          size={ChipSize.TABLE_CELL}
          displayNameOverride={getDisplayNameOverride(typedValue)}
        />
      );
    }
    case SchemaFieldConstants.FieldType.SELECTOR: {
      return <SelectorInternalLink name={typedValue.value ?? ''} />;
    }
    case BaseTypedValueType.STORABLES: {
      return (
        <span>
          {typedValue.storableSummaries.map((storableSummary) => (
            <StorableChip
              id={storableSummary.id}
              key={storableSummary.id}
              size={ChipSize.TABLE_CELL}
              displayNameOverride={getDisplayNameOverride(storableSummary)}
            />
          ))}
        </span>
      );
    }
    case BaseTypedValueType.STUDIES: {
      return <StudyChipList studies={typedValue.studies} isExportView={isExportView} />;
    }
    case SchemaFieldConstants.FieldType.BLOB_LINK:
      return (
        <BlobLinkListInTable
          blobs={
            ('targetBlobs' in typedValue &&
              typedValue.targetBlobs.map((blob) => restBlobToGraphQL(convertTypedValueBlob(blob)))) ||
            []
          }
          chipSize={ChipSize.TABLE_CELL}
          owningEntityApiId={fileId}
        />
      );
    case BaseTypedValueType.NUMBER: {
      if (
        columnConstraint.structuredTableColumn &&
        'unitApiIdentifier' in columnConstraint.structuredTableColumn &&
        columnConstraint.structuredTableColumn.unitApiIdentifier
      ) {
        return (
          <UnitAwareNumberTagValueViewer
            value={formatTypedValue(typedValue)}
            unitApiIdentifier={columnConstraint.structuredTableColumn.unitApiIdentifier}
          />
        );
      }
      return null;
    }
    case BaseTypedValueType.STRING:
    case BaseTypedValueType.BOOLEAN:
    default:
      return null;
  }
}

function formattedCellValueFromString(
  cell: Cell,
  displayString: string,
  wrapText: boolean = true
): FormattedCellValue {
  if (cell.link && isValidLink(cell.link)) {
    return (
      <BenchLink key="link" href={cell.link}>
        {displayString}
      </BenchLink>
    );
  }
  if (maybeContainsURL(displayString)) {
    return <UrlAwareText textValue={displayString} />;
  }
  // If the display string is empty we render as an inline block to ensure that the cell content occupies a
  // consistent minimum height.
  if (displayString === '') {
    return <span style={{display: 'inline-block'}} />;
  }
  return wrapText ? <span>{displayString}</span> : <span style={ELLIPSIS_TEXT_STYLE}>{displayString}</span>;
}

function RegistrationTableExistingBioentityCellPlaceholder({
  structuredTableKind,
  tagSchemaOrInterface,
  isReadOnly,
}: {
  structuredTableKind: StructuredTableKind | null;
  tagSchemaOrInterface: SchemaSummary | 'ALL';
  isReadOnly: boolean;
}): JSX.Element {
  const schemaName = tagSchemaOrInterface === 'ALL' ? 'entity' : tagSchemaOrInterface.name;

  if (structuredTableKind != null) {
    // Existing bioentity columns in StructuredTables require a value iff they are writeable.
    if (isReadOnly) {
      return <span>New {schemaName}</span>;
    } else {
      // Display the CTA even if the cell is locked for other reasons (e.g. viewing an older entry
      // version) to accurately reflect the UI at the time.
      return <span>Choose {prependAOrAn(schemaName)}...</span>;
    }
  } else {
    // In other tables, an existing bioentity column may be left blank to create a new bioentity,
    // whether or not it's read-only.
    return <span>New {schemaName}</span>;
  }
}

export function isInteractiveFormattedValue(
  columnConstraint: ColumnConstraint | null,
  cell: Cell,
  typedValue: TypedValue
): boolean {
  return (
    Boolean(columnConstraint && columnConstraint.columnDefinition) ||
    (cell.link && isValidLink(cell.link)) ||
    maybeContainsURL(formatTypedValue(typedValue, cell))
  );
}

function maybeContainsURL(displayString: string): boolean {
  return MAYBE_CONTAINS_URL_REGEX.test(displayString);
}

function getSkeletonChipOrRealValue(
  typedValue: TypedValue,
  formattedValue: FormattedCellValue,
  hasRowBeenInView?: Optional<boolean>
): JSX.Element | string | null {
  if (
    hasRowBeenInView != null &&
    !hasRowBeenInView &&
    !isTypedValueEmpty(typedValue) &&
    formattedValue != null
  ) {
    // Return skeleton chip until we're in the View and need to render the real value
    return <Chip faded={false} size={ChipSize.MINI} text="" />;
  }
  return formattedValue;
}

/**
 * Return the React element to render for this cell.
 */
export default function getFormattedCellValue(
  columnConstraint: ColumnConstraint | null,
  cell: Cell,
  typedValue: TypedValue | null,
  cellStateInfo: CellStateInfo,
  rowMetadata: RowMetadata | null,
  fileId: string = ''
): FormattedCellValue {
  if (typedValue == null) {
    return null;
  }
  if (columnConstraint && columnConstraint.columnDefinition) {
    const formattedValue = formattedCellValueFromColumnConstraint(
      columnConstraint,
      cell,
      typedValue,
      cellStateInfo,
      rowMetadata,
      fileId
    );

    if (formattedValue != null) {
      return getSkeletonChipOrRealValue(typedValue, formattedValue, cellStateInfo.hasRowBeenInView);
    }
  }
  return formattedCellValueFromString(
    cell,
    formatTypedValue(typedValue, cell),
    shouldWrapText(columnConstraint)
  );
}
